import React, { useState, useEffect } from 'react';
import { X, Share, Plus } from 'lucide-react';

// Detect iOS Safari
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isInStandaloneMode = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
const isMobile = () => /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent) || window.innerWidth < 768;

export const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOSDevice, setIsIOSDevice] = useState(false);

  useEffect(() => {
    // Don't show if already installed or not mobile
    if (isInStandaloneMode() || !isMobile()) return;

    if (isIOS()) {
      setIsIOSDevice(true);
      setShowBanner(true);
      return;
    }

    // Android / Chrome
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Also check if appinstalled fires
    window.addEventListener('appinstalled', () => {
      setShowBanner(false);
      setDeferredPrompt(null);
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowBanner(false);
        setDeferredPrompt(null);
      }
    }
  };

  if (!showBanner) return null;

  return (
    <div
      data-testid="install-prompt-banner"
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-red-600 shadow-2xl px-4 py-4"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 16px)' }}
    >
      <div className="max-w-lg mx-auto">
        <div className="flex items-start gap-3">
          <img
            src="/icon-192.png"
            alt="ГАРАНТ АД"
            className="w-12 h-12 rounded-xl border border-slate-200 flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-slate-900 text-sm">Добави на началния екран</p>
            <p className="text-slate-600 text-xs mt-0.5">
              {isIOSDevice
                ? 'Натиснете "Сподели" после "Добави към начален екран"'
                : 'Инсталирайте приложението за по-бърз достъп'}
            </p>
            {isIOSDevice && (
              <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-500">
                <span>Натиснете</span>
                <Share className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <span>след това</span>
                <span className="inline-flex items-center gap-0.5 border border-slate-300 rounded px-1 py-0.5 text-xs">
                  <Plus className="w-3 h-3" />Добави към начален екран
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {!isIOSDevice && (
              <button
                onClick={handleInstall}
                className="bg-red-600 text-white text-sm font-bold px-4 py-2 rounded-lg hover:bg-red-700 active:scale-95 transition-transform"
              >
                Инсталирай
              </button>
            )}
            <button
              onClick={() => setShowBanner(false)}
              className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg"
              aria-label="Затвори"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
